import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  LinearProgress,
  Alert,
  Divider
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  WalteroCard,
  WalteroTypography,
  WalteroSkeleton,
  WalteroChart
} from './waltero';
import billingService from '../services/billingService';
import {
  TrendingUp as TrendingUpIcon,
  Devices as DevicesIcon,
  Speed as SpeedIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';

/**
 * Usage Dashboard
 *
 * Displays subscription usage analytics and sensor utilization
 */
const UsageDashboard = ({ organizationId }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSubscriptions, setActiveSubscriptions] = useState([]);
  const [usageData, setUsageData] = useState(null);

  // Load data
  useEffect(() => {
    if (organizationId) {
      loadUsageData();
    } else {
      setLoading(false);
      setActiveSubscriptions([]);
      setUsageData(null);
    }
  }, [organizationId]);

  const loadUsageData = async () => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Load active subscriptions
      const activeData = await billingService.getActiveSubscriptions(organizationId);
      setActiveSubscriptions(activeData.activeSubscriptions || []);

      // For now, generate mock usage data
      // In production, this would come from actual sensor assignment data
      const mockUsage = generateMockUsageData(activeData.activeSubscriptions);
      setUsageData(mockUsage);
    } catch (err) {
      console.error('Error loading usage data:', err);
      setError(err.error || 'Failed to load usage data');
    } finally {
      setLoading(false);
    }
  };

  // Generate mock usage data for demonstration
  const generateMockUsageData = (subscriptions) => {
    if (!subscriptions || subscriptions.length === 0) return null;

    const sub = subscriptions[0];
    const utilizationPercent = Math.round((sub.assignedSensorCount / sub.purchasedSensorQuantity) * 100);

    return {
      totalSensors: sub.purchasedSensorQuantity,
      assignedSensors: sub.assignedSensorCount,
      activeSensors: Math.floor(sub.assignedSensorCount * 0.9), // 90% active
      utilizationPercent,
      trend: [
        { month: 'Jan', usage: 45 },
        { month: 'Feb', usage: 52 },
        { month: 'Mar', usage: 58 },
        { month: 'Apr', usage: 65 },
        { month: 'May', usage: 72 },
        { month: 'Jun', usage: utilizationPercent }
      ],
      deviceTypes: [
        { type: 'Temperature Sensors', count: Math.floor(sub.assignedSensorCount * 0.4) },
        { type: 'Humidity Sensors', count: Math.floor(sub.assignedSensorCount * 0.3) },
        { type: 'Pressure Sensors', count: Math.floor(sub.assignedSensorCount * 0.2) },
        { type: 'Other', count: Math.floor(sub.assignedSensorCount * 0.1) }
      ]
    };
  };

  // Calculate utilization color
  const getUtilizationColor = (percent) => {
    if (percent >= 90) return theme.palette.error.main;
    if (percent >= 75) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  if (loading) {
    return (
      <Box>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} md={6} key={i}>
              <WalteroSkeleton variant="rectangular" height={200} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (activeSubscriptions.length === 0) {
    return (
      <WalteroCard sx={{ p: 4, textAlign: 'center' }}>
        <AssessmentIcon
          sx={{
            fontSize: 64,
            color: theme.palette.grey[400],
            mb: 2
          }}
        />
        <Typography variant="h6" gutterBottom>
          No Active Subscriptions
        </Typography>
        <Typography color="textSecondary">
          Activate a subscription to view usage analytics
        </Typography>
      </WalteroCard>
    );
  }

  const subscription = activeSubscriptions[0]; // Use first active subscription for now

  return (
    <Box>
      {/* Header */}
      <WalteroTypography variant="h5" fontWeight="bold" gutterBottom>
        Usage Dashboard
      </WalteroTypography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Monitor your sensor utilization and track usage trends
      </Typography>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Total Capacity */}
        <Grid item xs={12} sm={6} md={3}>
          <WalteroCard sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <DevicesIcon sx={{ color: theme.palette.waltero.primary.coreBlue, mr: 2 }} />
              <Typography variant="subtitle2" color="textSecondary">
                Total Capacity
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight="bold">
              {usageData?.totalSensors || 0}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              sensors purchased
            </Typography>
          </WalteroCard>
        </Grid>

        {/* Assigned Sensors */}
        <Grid item xs={12} sm={6} md={3}>
          <WalteroCard sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <SpeedIcon sx={{ color: theme.palette.success.main, mr: 2 }} />
              <Typography variant="subtitle2" color="textSecondary">
                Assigned
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight="bold">
              {usageData?.assignedSensors || 0}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              sensors in use
            </Typography>
          </WalteroCard>
        </Grid>

        {/* Available Sensors */}
        <Grid item xs={12} sm={6} md={3}>
          <WalteroCard sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <TrendingUpIcon sx={{ color: theme.palette.info.main, mr: 2 }} />
              <Typography variant="subtitle2" color="textSecondary">
                Available
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight="bold">
              {subscription.availableQuantity || 0}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              sensors available
            </Typography>
          </WalteroCard>
        </Grid>

        {/* Utilization Rate */}
        <Grid item xs={12} sm={6} md={3}>
          <WalteroCard sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <AssessmentIcon
                sx={{
                  color: getUtilizationColor(usageData?.utilizationPercent || 0),
                  mr: 2
                }}
              />
              <Typography variant="subtitle2" color="textSecondary">
                Utilization
              </Typography>
            </Box>
            <Typography variant="h4" fontWeight="bold">
              {usageData?.utilizationPercent || 0}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={usageData?.utilizationPercent || 0}
              sx={{
                mt: 1,
                height: 6,
                borderRadius: 3,
                backgroundColor: theme.palette.grey[200],
                '& .MuiLinearProgress-bar': {
                  backgroundColor: getUtilizationColor(usageData?.utilizationPercent || 0)
                }
              }}
            />
          </WalteroCard>
        </Grid>
      </Grid>

      {/* Usage Trend Chart */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <WalteroCard sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Utilization Trend
            </Typography>
            <Divider sx={{ mb: 3 }} />
            {usageData?.trend && (
              <WalteroChart
                type="line"
                data={usageData.trend}
                config={{
                  xAxis: { key: 'month', label: 'Month' },
                  yAxis: { key: 'usage', label: 'Usage %' },
                  height: 300,
                  showGrid: true,
                  smooth: true,
                  color: theme.palette.waltero.primary.coreBlue
                }}
              />
            )}
          </WalteroCard>
        </Grid>

        {/* Device Type Breakdown */}
        <Grid item xs={12} lg={4}>
          <WalteroCard sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Sensor Types
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Box>
              {usageData?.deviceTypes?.map((type, index) => (
                <Box key={index} mb={2}>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2">{type.type}</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {type.count}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(type.count / usageData.assignedSensors) * 100}
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      backgroundColor: theme.palette.grey[200]
                    }}
                  />
                </Box>
              ))}
            </Box>
          </WalteroCard>
        </Grid>
      </Grid>

      {/* Recommendations */}
      {usageData?.utilizationPercent > 80 && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>High Utilization Alert:</strong> You're using {usageData.utilizationPercent}% of your
            sensor capacity. Consider increasing your subscription quantity to ensure room for growth.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default UsageDashboard;